/**
 * Escolher uma imagem para um plugin (imagem editável da ficha, `Dialogs.selectImageURL`), o mapa ou
 * a aura: enviar do computador (fica ligada à mesa, para todos verem), uma das imagens que já estão
 * na mesa, ou um endereço da web. Com `cloud` (a capa e a marca da mesa), a primeira aba é o Desk
 * Cloud de quem escolhe, no lugar das imagens da mesa, e `rules` diz que imagem serve: o envio reduz
 * a que passa do maior lado, e as do Cloud fora das regras ficam apagadas, com o motivo.
 *
 * Imagens do RPG Desk voltam como `rpgdesk-file:<id>`: o link de verdade é pedido na hora de
 * desenhar (os links assinados expiram). Com `importWeb` (a textura dos dados), o endereço da web
 * também volta assim: uma cópia da imagem vai para o Desk Cloud.
 */
import { useEffect, useState } from 'react';
import { api } from '../api.js';
import { formatBytes } from '../bytes.js';
import { CloudPickCrumbs, CloudPickFolders } from '../cloud/CloudFilePicker.js';
import { useCloudListing } from '../cloud/useCloudListing.js';
import { fitImage, uploadMyFile, uploadRoomFile, useFileUrl, type ImageRules } from '../files.js';
import { useT } from '../i18n/index.js';
import { fetchDroppedImage, imageFileName, nameFromUrl } from '../room/droppedImage.js';
import { serverUrl } from '../serverUrl.js';
import { DESK_FILE_REF, type DeskFile } from '../shared.js';
import { useApp } from '../store.js';
import { Button, Dialog, TextEdit } from './controls.js';
import { Icon } from './icons.js';

export const RPGDESK_FILE = DESK_FILE_REF;
/** id do arquivo de um valor `rpgdesk-file:<id>`, ou null */
export const rpgDeskFileId = (value: unknown): string | null => (typeof value === 'string' && value.startsWith(RPGDESK_FILE) ? value.slice(RPGDESK_FILE.length) || null : null);

type Tab = 'cloud' | 'upload' | 'room' | 'web';
type Translate = (key: string, params?: Record<string, string | number>) => string;

/**
 * `allowWeb: false` tira a aba do endereço da web (plugins web não carregam imagens de fora:
 * docs/spec/web-plugins.md, "Internet"). `linkRoom: false` envia do computador sem ligar à mesa
 * aberta (a capa do perfil). `onFile` recebe o arquivo do computador no lugar do envio ao Desk
 * Cloud (a foto do perfil, que tem o envio dela), e `uploadHint` troca a dica dessa aba.
 * `importWeb` guarda uma cópia da imagem do endereço da web: ela é baixada (de outro site, pela
 * cópia que o servidor busca), reduzida pelas `rules` e enviada como as do computador.
 */
export function ImagePickerDialog({
  initial,
  title,
  onPick,
  onCancel,
  allowWeb = true,
  cloud = false,
  rules,
  linkRoom = true,
  onFile,
  uploadHint,
  importWeb = false,
}: {
  initial?: string;
  title?: string;
  onPick: (value: string) => void;
  onCancel: () => void;
  allowWeb?: boolean;
  cloud?: boolean;
  rules?: ImageRules;
  linkRoom?: boolean;
  onFile?: (file: File) => void;
  uploadHint?: string;
  importWeb?: boolean;
}) {
  const t = useT();
  const token = useApp((s) => s.token);
  const roomId = useApp((s) => s.activeRoomId);
  const tabs: Tab[] = [...(cloud ? (['cloud', 'upload'] as const) : (['upload', 'room'] as const)), ...(allowWeb ? (['web'] as const) : [])];
  const [tab, setTab] = useState<Tab>(allowWeb && initial && /^https?:/i.test(initial) ? 'web' : tabs[0]!);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [url, setUrl] = useState(initial && /^https?:/i.test(initial) ? initial : '');
  const [files, setFiles] = useState<DeskFile[] | null>(null);

  useEffect(() => {
    if (tab !== 'room' || files || !token || !roomId) return;
    // o painel da mesa: os mestres veem as imagens dos mestres; os outros, as suas (docs/spec/desk-cloud.md)
    api
      .roomFiles(token, roomId)
      .then(({ files: list }) => setFiles(list.filter((f) => f.mime.startsWith('image/'))))
      .catch(() => setFiles([]));
  }, [tab, files, token, roomId]);

  /** Reduz pelas regras, envia ao Desk Cloud e escolhe o arquivo enviado. O erro fica no diálogo. */
  const send = async (file: () => Promise<File>) => {
    setBusy(true);
    setError(null);
    try {
      const picked = await file();
      const fitted = rules ? await fitImage(picked, rules) : picked;
      const stored = await (linkRoom ? uploadRoomFile(fitted) : uploadMyFile(fitted));
      onPick(`${RPGDESK_FILE}${stored.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const upload = async (file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError(t('imagePicker.notImage'));
      return;
    }
    if (onFile) {
      onFile(file);
      return;
    }
    await send(async () => file);
  };

  const webOk = /^https?:\/\/\S+$/i.test(url.trim());
  /** O endereço da web: ele mesmo, ou (`importWeb`) a cópia da imagem no Desk Cloud. */
  const pickWeb = () => {
    const href = url.trim();
    if (!importWeb) {
      onPick(href);
      return;
    }
    void send(async () => {
      const blob = await fetchDroppedImage(href, token).catch(() => null);
      if (!blob) throw new Error(t('imagePicker.webFailed'));
      return new File([blob], imageFileName(nameFromUrl(href), blob.type, t('chat.drop.imageName')), { type: blob.type });
    });
  };
  return (
    <Dialog
      title={title ?? t('imagePicker.title')}
      onClose={onCancel}
      wide={cloud}
      actions={
        <>
          <Button variant="secondary" onClick={onCancel}>
            {t('create.cancel')}
          </Button>
          {tab === 'web' ? (
            <Button variant="action" disabled={!webOk || busy} onClick={pickWeb}>
              {t(importWeb ? (busy ? 'imagePicker.importing' : 'imagePicker.import') : 'imagePicker.use')}
            </Button>
          ) : null}
        </>
      }
    >
      <div className="rd-imgpick">
        <div className="rd-imgpick__tabs" role="tablist">
          {tabs.map((id) => (
            <button key={id} type="button" role="tab" aria-selected={tab === id} className={tab === id ? 'is-on' : ''} onClick={() => setTab(id)}>
              {t(`imagePicker.tab.${id}`)}
            </button>
          ))}
        </div>
        {tab === 'cloud' ? <CloudImages rules={rules} onPick={(id) => onPick(`${RPGDESK_FILE}${id}`)} onUpload={() => setTab('upload')} /> : null}
        {tab === 'upload' ? (
          <>
            <label className={`rd-btn rd-btn--action rd-filePick${busy ? ' is-busy' : ''}`}>
              {busy ? t('imagePicker.uploading') : t('imagePicker.choose')}
              <input type="file" hidden accept="image/*" disabled={busy} onChange={(e) => void upload(e.target.files?.[0])} />
            </label>
            {uploadHint || cloud ? <p className="rd-imgpick__hint">{uploadHint ?? t('imagePicker.uploadHint')}</p> : null}
          </>
        ) : null}
        {tab === 'room' ? (
          files === null ? (
            <p className="rd-imgpick__hint">{t('imagePicker.loading')}</p>
          ) : files.length === 0 ? (
            <p className="rd-imgpick__hint">{t('imagePicker.noRoomImages')}</p>
          ) : (
            <ul className="rd-imgpick__grid">
              {files.map((f) => (
                <li key={f.id}>
                  <button type="button" title={f.name} aria-label={f.name} onClick={() => onPick(`${RPGDESK_FILE}${f.id}`)}>
                    <Thumb fileId={f.id} />
                  </button>
                </li>
              ))}
            </ul>
          )
        ) : null}
        {tab === 'web' ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (webOk && !busy) pickWeb();
            }}
          >
            <TextEdit look="filled" value={url} onChange={setUrl} placeholder="https://…" aria-label={t('imagePicker.url')} autoFocusOnMount />
            {importWeb ? <p className="rd-imgpick__hint">{t('imagePicker.importHint')}</p> : null}
          </form>
        ) : null}
        {error ? <p className="rd-imgpick__error">{error}</p> : null}
      </div>
    </Dialog>
  );
}

function Thumb({ fileId }: { fileId: string }) {
  const url = useFileUrl(fileId);
  return typeof url === 'string' && url !== 'error' ? <img src={url} alt="" /> : <span className="rd-imgpick__ph" />;
}

/** Por que a imagem do Cloud não serve (o tipo, o tamanho) e o selo curto no canto dela, ou null. */
function refusal(file: DeskFile, rules: ImageRules | undefined, t: Translate): { why: string; badge: string } | null {
  if (!rules) return null;
  if (!rules.types.includes(file.mime)) return { why: t('imagePicker.badType'), badge: (file.name.split('.').pop() ?? '').toUpperCase() };
  if (file.size > rules.maxBytes) return { why: t('imagePicker.tooBig', { max: formatBytes(rules.maxBytes) }), badge: formatBytes(file.size) };
  return null;
}

/**
 * As imagens do Desk Cloud de quem escolhe, as mais novas primeiro, com as pastas em cima (como no
 * seletor do chat), a busca no nome e "Mostrar mais" (páginas de 100). A miniatura, quando há; senão
 * a própria imagem, só se ela serve (uma fora do tamanho não é baixada à toa).
 */
function CloudImages({ rules, onPick, onUpload }: { rules: ImageRules | undefined; onPick: (fileId: string) => void; onUpload: () => void }) {
  const t = useT();
  const listing = useCloudListing({ images: true });
  const { files, tiles, folderId, q } = listing;
  const folder = listing.path[listing.path.length - 1];
  const search = folder ? t('cloud.search', { where: folder.name }) : t('imagePicker.search');

  return (
    <div className="rd-imgpick__cloud">
      <label className="rd-searchBox rd-imgpick__search">
        <Icon name="search" size={15} />
        <input type="search" value={listing.query} onChange={(e) => listing.setQuery(e.target.value)} placeholder={search} aria-label={search} />
      </label>
      <CloudPickCrumbs listing={listing} />
      <div className="rd-imgpick__browse">
        <CloudPickFolders listing={listing} />
        {files === null ? (
          <p className="rd-imgpick__hint">{t('imagePicker.loading')}</p>
        ) : files.length === 0 ? (
          <div className="rd-imgpick__empty">
            <p className="rd-imgpick__hint">{t(q ? 'imagePicker.noMatch' : folderId ? 'imagePicker.noFolderImages' : 'imagePicker.noCloudImages')}</p>
            {q || folderId ? null : (
              <Button variant="secondary" onClick={onUpload}>
                {t('imagePicker.tab.upload')}
              </Button>
            )}
          </div>
        ) : (
          <section aria-label={t(folderId ? 'imagePicker.images' : 'imagePicker.allImages')}>
            {tiles.length ? <h3 className="rd-cloudSection__title">{t(folderId ? 'imagePicker.images' : 'imagePicker.allImages')}</h3> : null}
            <ul className="rd-imgpick__grid">
              {files.map((file) => {
                const off = refusal(file, rules, t);
                const src = file.thumbUrl ?? (off ? null : file.url);
                const label = off ? `${file.name} — ${off.why}` : file.name;
                return (
                  <li key={file.id}>
                    <button type="button" className={off ? 'is-off' : undefined} disabled={!!off} title={label} aria-label={label} onClick={() => onPick(file.id)}>
                      {src ? <img src={serverUrl(src)} alt="" loading="lazy" /> : <span className="rd-imgpick__ph" />}
                      {off ? <span className="rd-imgpick__why">{off.badge}</span> : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        )}
      </div>
      {listing.hasMore ? (
        <Button variant="secondary" className="rd-imgpick__more" busy={listing.loadingMore} onClick={listing.more}>
          {t('imagePicker.more')}
        </Button>
      ) : null}
    </div>
  );
}
